import { IconButton24, IconNames8 } from '@pinpointhq/thumbtack';
import * as classnames from 'classnames';
import * as pluralize from 'pluralize';
import * as React from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { humanize } from '../../../shared/utilities/humanize';
import { useFilterContext } from '../FilterContext';
import { Dropdown } from './Dropdown';

interface IProps {
  isOpen: boolean;
  name?: string;
  translations?: {
    singleItem: string;
    pluralItems: string;
    allItems: string;
  };
}

export function Presenter({ isOpen, name, translations }: IProps) {
  const { handleFilterClear, handleFilterClose, handleVisibilityToggle, selectedItemValues, items, handleItemSelect } =
    useFilterContext();

  const [focusIndex, setFocusIndex] = React.useState<number>(0);

  const filterClass = classnames('filter', { 'filter--active': selectedItemValues.length > 0 });

  let buttonText: string;
  const numberOfSelectedItems = selectedItemValues.length;

  if (translations) {
    if (numberOfSelectedItems === 0) {
      buttonText = translations.allItems;
    } else if (numberOfSelectedItems === 1) {
      buttonText = `${numberOfSelectedItems} ${translations.singleItem}`;
    } else {
      buttonText = `${numberOfSelectedItems} ${translations.pluralItems}`;
    }
  } else {
    buttonText = `${numberOfSelectedItems || 'All'} ${pluralize(humanize(name), numberOfSelectedItems)}`;
  }

  const caret = isOpen ? 'up' : 'down';

  const clearButton = <IconButton24 onClick={handleFilterClear} icon={IconNames8.CROSS} />;

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === 'ArrowDown') {
      handleArrowKeys(event, 1);
    }
    if (event.key === 'ArrowUp') {
      handleArrowKeys(event, -1);
    }
    if (event.key === 'Enter') {
      handleItemSelect(items[focusIndex]?.value);
    }
    if (event.key === 'Escape') {
      handleFilterClose();
    }
    if (event.keyCode === 32 && isOpen) {
      event.preventDefault();
      handleItemSelect(items[focusIndex]?.value);
    }
    // This allows you to tab off the element once its open
    if (event.key === 'Tab' && isOpen) {
      handleVisibilityToggle();
    }
  };

  const handleArrowKeys = (event: React.KeyboardEvent<HTMLDivElement>, offset: number) => {
    event.preventDefault();
    if (!isOpen) {
      handleVisibilityToggle();
      // Each subsequent time the down arrow is pressed we want to change the focus index
      //
    } else if (focusIndex === 0 && offset < 0) {
      setFocusIndex(items.length - 1);
    } else if (items.length <= focusIndex + offset) {
      setFocusIndex(0);
    } else {
      setFocusIndex(focusIndex + offset);
    }
  };

  return (
    <OutsideClickHandler onOutsideClick={handleFilterClose}>
      <div className={filterClass}>
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
        <div className="filter__button" onClick={handleVisibilityToggle} tabIndex={0} onKeyDown={handleKeyDown}>
          <div className="frow frow--justify-between frow--nowrap">
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{buttonText}</span>
            <div className="frow frow--items-center frow--nowrap">
              {selectedItemValues.length > 0 && clearButton}
              <i className={`fal fa-fw fa-angle-${caret} filter__caret`} />
            </div>
          </div>
        </div>
        {isOpen && <Dropdown focusIndex={focusIndex} setFocusIndex={setFocusIndex} />}
      </div>
    </OutsideClickHandler>
  );
}
